import { useEffect, useState } from "react";
import api, { formatEuro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Plus, Save } from "lucide-react";
import { toast } from "sonner";

const CONDITIONS = [
  { value: "has_remote", label: "Avec télécommande", vtype: "bool" },
  { value: "all_keys_lost", label: "Toutes clés perdues", vtype: "bool" },
  { value: "year_gte", label: "Année ≥", vtype: "number" },
  { value: "year_lte", label: "Année ≤", vtype: "number" },
  { value: "brand", label: "Marque", vtype: "text" },
  { value: "model", label: "Modèle", vtype: "text" },
  { value: "travel_zone", label: "Zone de déplacement", vtype: "zone" },
  { value: "urgency", label: "Urgence", vtype: "urgency" },
];

export default function Services() {
  const [services, setServices] = useState([]);
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);

  async function load() {
    const [s, r] = await Promise.all([api.get("/services"), api.get("/pricing-rules")]);
    setServices(s.data); setRules(r.data);
  }
  useEffect(() => { load(); }, []);

  async function saveService(s) {
    await api.put(`/services/${s.id}`, {
      code: s.code, name: s.name, base_price: Number(s.base_price),
      duration_minutes: Number(s.duration_minutes), instant_quote: s.instant_quote, active: s.active,
    });
    toast.success("Prestation enregistrée");
    load();
  }

  function newRule() {
    setEditRule({ id: null, service_code: null, label: "", condition_type: "has_remote", condition_value: true, adjustment: 0, active: true });
    setOpen(true);
  }
  function editExisting(r) { setEditRule({ ...r }); setOpen(true); }

  async function saveRule() {
    const r = editRule;
    const payload = {
      service_code: r.service_code || null,
      label: r.label,
      condition_type: r.condition_type,
      condition_value: r.condition_value,
      adjustment: Number(r.adjustment),
      active: r.active,
    };
    if (r.id) await api.put(`/pricing-rules/${r.id}`, payload);
    else await api.post("/pricing-rules", payload);
    toast.success("Règle enregistrée");
    setOpen(false); load();
  }

  async function delRule(r) {
    if (!confirm("Supprimer cette règle ?")) return;
    await api.delete(`/pricing-rules/${r.id}`);
    load();
  }

  const cond = CONDITIONS.find((c) => c.value === (editRule?.condition_type)) || CONDITIONS[0];

  return (
    <div>
      <h1 className="font-display text-3xl font-extrabold text-[#16324F]">Prestations & tarifs</h1>
      <p className="mt-1 text-slate-500">Prix de base et ajustements. Le calcul se fait en direct sur votre parcours public.</p>

      <section className="mt-8">
        <h2 className="font-display font-bold text-xl text-[#16324F]">Prestations</h2>
        <div className="mt-4 grid gap-3">
          {services.map((s) => (
            <div key={s.id} className="kf-card p-4 grid md:grid-cols-[1fr_140px_100px_130px_100px] gap-3 items-center" data-testid={`service-row-${s.code}`}>
              <div>
                <div className="font-semibold text-[#16324F]">{s.name}</div>
                <div className="text-xs text-slate-500 font-mono">{s.code}</div>
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">Prix TTC</Label>
                <Input type="number" value={s.base_price} onChange={(e) => setServices(services.map((x) => x.id === s.id ? {...x, base_price: e.target.value} : x))} className="h-9"/>
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">Durée (min)</Label>
                <Input type="number" value={s.duration_minutes} onChange={(e) => setServices(services.map((x) => x.id === s.id ? {...x, duration_minutes: e.target.value} : x))} className="h-9"/>
              </div>
              <div className="flex items-center gap-2 text-sm"><Switch checked={s.instant_quote} onCheckedChange={(v) => setServices(services.map((x) => x.id === s.id ? {...x, instant_quote: v} : x))}/> Devis instantané</div>
              <Button size="sm" onClick={() => saveService(s)} className="rounded-full bg-[#16324F] hover:bg-[#0F2438]" data-testid={`save-service-${s.code}`}><Save className="w-4 h-4 mr-1"/>Enregistrer</Button>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-xl text-[#16324F]">Ajustements tarifaires</h2>
          <Button onClick={newRule} className="rounded-full bg-[#19C3B3] hover:bg-[#0F9E90] text-white" data-testid="new-rule"><Plus className="w-4 h-4 mr-1"/>Nouvelle règle</Button>
        </div>
        <div className="mt-4 kf-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Libellé</th>
                <th className="text-left px-4 py-3 font-semibold">Condition</th>
                <th className="text-left px-4 py-3 font-semibold">Prestation</th>
                <th className="text-right px-4 py-3 font-semibold">Ajustement</th>
                <th className="text-left px-4 py-3 font-semibold">Actif</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/70">
                  <td className="px-4 py-3 font-semibold text-[#16324F] cursor-pointer" onClick={() => editExisting(r)}>{r.label}</td>
                  <td className="px-4 py-3 text-slate-600">{CONDITIONS.find((c) => c.value === r.condition_type)?.label} = <span className="font-mono">{String(r.condition_value)}</span></td>
                  <td className="px-4 py-3 text-slate-600">{r.service_code ? services.find((s) => s.code === r.service_code)?.name : <span className="italic text-slate-400">Toutes</span>}</td>
                  <td className="px-4 py-3 text-right font-semibold" style={{color: r.adjustment >= 0 ? "#16324F" : "#B84324"}}>{r.adjustment >= 0 ? "+" : ""}{formatEuro(r.adjustment)}</td>
                  <td className="px-4 py-3">{r.active ? <span className="text-emerald-600">●</span> : <span className="text-slate-300">●</span>}</td>
                  <td className="px-4 py-3 text-right"><button onClick={() => delRule(r)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4"/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editRule?.id ? "Modifier la règle" : "Nouvelle règle"}</DialogTitle></DialogHeader>
          {editRule && (
            <div className="grid gap-4">
              <div className="grid gap-1.5"><Label>Libellé</Label><Input value={editRule.label} onChange={(e) => setEditRule({...editRule, label: e.target.value})} data-testid="rule-label"/></div>
              <div className="grid gap-1.5">
                <Label>Prestation</Label>
                <Select value={editRule.service_code || "all"} onValueChange={(v) => setEditRule({...editRule, service_code: v === "all" ? null : v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les prestations</SelectItem>
                    {services.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-1.5">
                  <Label>Condition</Label>
                  <Select value={editRule.condition_type} onValueChange={(v) => setEditRule({...editRule, condition_type: v, condition_value: v === "has_remote" || v === "all_keys_lost" ? true : v === "year_gte" || v === "year_lte" ? 2020 : ""})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {CONDITIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label>Valeur</Label>
                  {cond.vtype === "bool" && (
                    <Select value={String(editRule.condition_value)} onValueChange={(v) => setEditRule({...editRule, condition_value: v === "true"})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent><SelectItem value="true">Oui</SelectItem><SelectItem value="false">Non</SelectItem></SelectContent>
                    </Select>
                  )}
                  {cond.vtype === "number" && <Input type="number" value={editRule.condition_value} onChange={(e) => setEditRule({...editRule, condition_value: Number(e.target.value)})}/>}
                  {cond.vtype === "text" && <Input value={editRule.condition_value} onChange={(e) => setEditRule({...editRule, condition_value: e.target.value})}/>}
                  {cond.vtype === "zone" && (
                    <Select value={editRule.condition_value} onValueChange={(v) => setEditRule({...editRule, condition_value: v})}>
                      <SelectTrigger><SelectValue placeholder="Zone"/></SelectTrigger>
                      <SelectContent><SelectItem value="zone1">Zone 1 (0-15 km)</SelectItem><SelectItem value="zone2">Zone 2 (15-30 km)</SelectItem></SelectContent>
                    </Select>
                  )}
                  {cond.vtype === "urgency" && (
                    <Select value={editRule.condition_value} onValueChange={(v) => setEditRule({...editRule, condition_value: v})}>
                      <SelectTrigger><SelectValue placeholder="Type"/></SelectTrigger>
                      <SelectContent><SelectItem value="standard">Standard</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent>
                    </Select>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-1.5"><Label>Ajustement (€)</Label><Input type="number" value={editRule.adjustment} onChange={(e) => setEditRule({...editRule, adjustment: e.target.value})} data-testid="rule-adjustment"/></div>
                <div className="grid gap-1.5"><Label>Actif</Label><Switch checked={editRule.active} onCheckedChange={(v) => setEditRule({...editRule, active: v})}/></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={saveRule} className="bg-[#16324F]" data-testid="rule-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
